import { useState, useCallback, useRef, useEffect } from 'react';
import { useFormBuilderStore } from '../store/form-builder.store';
import { useMappingsStore } from '../store/mappings.store';
import { useValidationStore } from '../store/validation.store';
import { logger } from '@/features/help/utils/logger';

export const useFormBuilder = () => {
  const fieldItemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Local state for grid-level validation modal
  const [gridLevelValidationModal, setGridLevelValidationModal] = useState<{
    isOpen: boolean;
    errors: string[];
  }>({
    isOpen: false,
    errors: [],
  });

  const {
    currentMappingStructure,
    clearFieldMappingInCurrentStructure,
    toggleFieldRequiredInCurrentMapping,
    loadStructureForCurrentMapping,
    activeFieldId,
    setActiveFieldId,
    expandedPageId,
    setExpandedPageId,
  } = useFormBuilderStore();

  const {
    mappingSummaries,
    currentFieldMappingId,
    isDrafting,
    isLoadingAvailableMappings,
    startNewMappingDraft,
    saveAndTestCurrentMapping,
  } = useMappingsStore();

  const dashboardInitialized = useFormBuilderStore((state) => state.dashboardInitialized);
  const { testResults, closeTestResults } = useValidationStore();

  // When the current mapping ID changes (i.e., user clicks a new tab),
  // load its structure from the cache. This ensures the UI is always in sync.
  useEffect(() => {
    if (dashboardInitialized) {
      loadStructureForCurrentMapping();
    }
  }, [currentFieldMappingId, dashboardInitialized, loadStructureForCurrentMapping]);

  // Scroll to the active field when it changes
  useEffect(() => {
    if (activeFieldId && fieldItemRefs.current[activeFieldId]) {
      fieldItemRefs.current[activeFieldId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center', // avoid overscrolling past sticky header
      });
    }
  }, [activeFieldId]);

  // Find and set the first unmapped field as active when the structure loads
  useEffect(() => {
    if (currentMappingStructure && !activeFieldId) {
      const allFields = currentMappingStructure.pages.flatMap((p) =>
        p.sections.flatMap((s) => s.fields)
      );
      const firstUnmapped = allFields.find((f) => !f.fieldApiName);
      if (firstUnmapped) {
        setActiveFieldId(firstUnmapped.id);
      } else {
        setActiveFieldId(null);
      }
    }
  }, [currentMappingStructure, setActiveFieldId]);

  useEffect(() => {
    logger.info('FormBuilder', 'Component Mount/Update', {
      currentFieldMappingId,
      isDrafting,
      mappingSummaries: mappingSummaries.length,
    });
  }, [currentFieldMappingId, mappingSummaries, isDrafting]);

  // Auto-create new mapping draft when no existing mappings are found
  useEffect(() => {
    // Only run this effect when:
    // 1. We're not currently loading mappings
    // 2. There are no available mappings
    // 3. We're not already in drafting mode
    // 4. Dashboard is initialized
    if (
      !isLoadingAvailableMappings &&
      mappingSummaries.length === 0 &&
      !isDrafting &&
      dashboardInitialized
    ) {
      logger.info('FormBuilder', 'No existing mappings found, automatically creating new draft');
      startNewMappingDraft();
    }
  }, [
    isLoadingAvailableMappings,
    mappingSummaries.length,
    isDrafting,
    dashboardInitialized,
    startNewMappingDraft,
  ]);

  const handleClearFormMapping = useCallback(
    (formFieldId: string) => {
      clearFieldMappingInCurrentStructure(formFieldId);
    },
    [clearFieldMappingInCurrentStructure]
  );

  const handleToggleFieldRequired = useCallback(
    (formFieldId: string) => {
      toggleFieldRequiredInCurrentMapping(formFieldId);
    },
    [toggleFieldRequiredInCurrentMapping]
  );

  const handleCreateNewMapping = () => {
    startNewMappingDraft();
    loadStructureForCurrentMapping();
  };

  const handleSaveCurrent = async () => {
    const result = await saveAndTestCurrentMapping();

    // If there are grid-level validation errors, show them in the local modal
    if (result.gridLevelErrors.length > 0) {
      setGridLevelValidationModal({
        isOpen: true,
        errors: result.gridLevelErrors,
      });
    }
  };

  const handleCloseGridLevelValidation = () => {
    setGridLevelValidationModal({
      isOpen: false,
      errors: [],
    });
  };

  const handleMappingComplete = (completedFieldId: string) => {
    if (!currentMappingStructure) return;

    const mappableFields = currentMappingStructure.pages
      .flatMap((p) => p.sections)
      .filter((s) => s.id !== 'final-requirements') // Exclude non-mappable sections
      .flatMap((s) => s.fields)
      .filter((f) => f.type !== 'signature' && f.type !== 'file'); // Exclude non-mappable field types

    const currentIndex = mappableFields.findIndex((f) => f.id === completedFieldId);

    if (currentIndex !== -1 && currentIndex < mappableFields.length - 1) {
      const nextField = mappableFields[currentIndex + 1];
      setActiveFieldId(nextField.id);
    } else {
      setActiveFieldId(null); // No more fields to map
    }
  };

  return {
    // State
    fieldItemRefs,
    gridLevelValidationModal,
    currentMappingStructure,
    activeFieldId,
    setActiveFieldId,
    expandedPageId,
    setExpandedPageId,
    dashboardInitialized,
    testResults,

    // Handlers
    handleClearFormMapping,
    handleToggleFieldRequired,
    handleCreateNewMapping,
    handleSaveCurrent,
    handleCloseGridLevelValidation,
    closeTestResults,
    handleMappingComplete,
  };
};
